import {
  Anchor,
  Box,
  Button,
  Group,
  Pill,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { ArrowSquareOutIcon, XIcon } from '@phosphor-icons/react';

import EntityLogo from 'components/common/entity-logo/EntityLogo';
import { isSafeLinkUrl } from 'modules/shared/libs/url/is-safe-link-url';

import styles from './CitationCard.module.css';
import { getCitationIcon, getExternalLinkLabel } from './CitationCard.utils';

import type { CitationCardProps } from './CitationCard.types';

/**
 * Displays a citation source card with title, description snippet, and external links.
 * Supports two treatments: sidepanel (with `number` badge) and inline popup (without).
 *
 * @example
 * // Sidepanel treatment (with number)
 * <CitationCard number={1} title="Gong Call" sourceType="gong" />
 *
 * // Inline popup treatment (no number)
 * <CitationCard title="Gong Call" sourceType="gong" />
 */
export default function CitationCard({
  number,
  title,
  description,
  displayDate,
  sources = [],
  sourceType,
  entityUrl,
  entityName,
  authorId,
  onRemove,
  removeDisabled = false,
}: CitationCardProps) {
  const SourceIcon = getCitationIcon(sourceType);

  return (
    <Box pos="relative">
      {/*
      Temporary disabling this as we need to update the design to support author avatars in the citation card.
      {authorId && (
        <Box className={styles.authorAvatar}>
          <UserAvatar userId={authorId} size={28} />
        </Box>
      )} */}

      <Stack gap="xs" p="sm">
        <Group gap="xs" align="flex-start" wrap="nowrap">
          {number !== undefined && (
            <Box className={styles.numberBadge}>{number}</Box>
          )}

          <Box c="gray.6">
            <SourceIcon
              size={18}
              color="var(--mantine-color-gray-6)"
              weight="regular"
            />
          </Box>

          <Box className={styles.headerContent}>
            <Group
              justify="space-between"
              align="flex-start"
              gap="xs"
              wrap="nowrap"
            >
              <Text className={styles.title} lineClamp={2}>
                {title}
              </Text>
              {displayDate && (
                <Text fz="xs" c="gray.5" style={{ whiteSpace: 'nowrap' }}>
                  {displayDate}
                </Text>
              )}
            </Group>
          </Box>
        </Group>

        <Stack gap="xs" ml={number !== undefined ? 34 : undefined}>
          {description && (
            <Text
              fz="xs"
              c="gray.6"
              lh={1.5}
              lineClamp={3}
              pl="xs"
              style={{ borderLeft: '2px solid var(--mantine-color-gray-3)' }}
            >
              {description}
            </Text>
          )}

          {(sources.length > 0 || entityUrl) && (
            <Group
              gap="xs"
              justify="space-between"
              align="center"
              wrap="nowrap"
            >
              <Pill.Group gap={6}>
                {sources.map((source) => {
                  // Source URLs come from the agent, so a non-web link
                  // (`javascript:`, `data:`) shows as text, unlinked.
                  if (!isSafeLinkUrl(source.url)) {
                    return (
                      <Pill key={source.url} className={styles.linkPill}>
                        {source.label ?? source.url}
                      </Pill>
                    );
                  }
                  return (
                    <Anchor
                      key={source.url}
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={source.url}
                      underline="never"
                    >
                      <Pill className={styles.linkPill}>
                        <ArrowSquareOutIcon size={12} />
                        {source.label ?? getExternalLinkLabel(source.url)}
                      </Pill>
                    </Anchor>
                  );
                })}
              </Pill.Group>

              {entityUrl && entityName && (
                <EntityLogo
                  name={entityName}
                  url={entityUrl}
                  size={24}
                  radius="md"
                />
              )}
            </Group>
          )}

          {onRemove && (
            <Group gap="xs" justify="flex-end" mt="xs">
              <Tooltip
                label="Chat is busy. Please wait for the current response to complete."
                disabled={!removeDisabled}
                withArrow
              >
                <Button
                  size="xs"
                  variant="subtle"
                  color="red"
                  leftSection={<XIcon size={12} />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemove();
                  }}
                  disabled={removeDisabled}
                >
                  Remove
                </Button>
              </Tooltip>
            </Group>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
